import React, { useMemo } from 'react'
import { ListItem } from '@fs/zion-ui'
import { HelperButton, HelperListItem } from '@fs/zion-helper-mode'
import { trackEvent } from '@fs/zion-analytics'
import { useUser } from '@fs/zion-user'
import { usePermission, useShowTemple } from '@fs/zion-permissions'
import { i18n } from '@fs/zion-locale'
import { useFeatureFlag } from '@fs/zion-flags'
import { visibleLinkCss, endCss, linkCss, buttonCss } from './styles'

// We might use collectionId from treeDetails for the findUrl in the future. Right now the formatting is different, so we hardcode it to 970
const COLLECTION_ID_IN_SEARCH_FORMAT = '970'

/**
 * Because the "More" menu uses <MenuOverlay> and its API calls for <ListItem>
 * children instead of <li> elements, we need to pull out the raw data so it
 * can be passed to either. This makes the component about 3x more complex. It
 * means we need this items data and we have to use React state to manage
 * splitting up the menu instead of just moving around DOM elements :(
 */
const subNavItems = {
  contributions: {
    page: 'contributions',
    'data-testid': 'nav-contributions',
    to: '/tree/contributions',
    external: true,
    wayfinding: true,
    linkName: 'Subnav: Contributions',
    label: 'subnav-legacy.subnav.contributions-link',
    className: visibleLinkCss,
  },
  familyGroups: {
    page: 'family',
    'data-testid': 'nav-family-groups',
    to: '/groups/family',
    external: true,
    wayfinding: true,
    linkName: 'Subnav: Family Groups',
    label: 'subnav-legacy.family-groups.label',
    className: visibleLinkCss,
  },
  find: {
    // `to` property is dynamic and will be added in the component
    page: 'find',
    'data-testid': 'nav-find',
    external: true,
    wayfinding: true,
    linkName: 'Subnav: Find',
    label: 'common-ui:find.action',
    className: visibleLinkCss,
  },
  following: {
    page: 'following',
    'data-testid': 'nav-following',
    to: '/tree/following',
    external: true,
    wayfinding: true,
    linkName: 'Subnav: Following',
    label: 'subnav-legacy.subnav.following-link',
    className: visibleLinkCss,
  },
  helper: {
    page: 'helper',
    as: (props) => <HelperButton {...props} asChip showIcon emphasis="low" />,
    moreAs: HelperListItem,
    'data-testid': 'helper-button',
    linkName: 'Subnav: Helper',
    listItemCss: endCss,
  },
  overview: {
    page: 'overview',
    'data-testid': 'nav-overview',
    to: '/tree/overview',
    external: true,
    wayfinding: true,
    linkName: 'Tree Subnav: Overview',
    label: 'subnav-legacy.subnav.overview-link',
    className: visibleLinkCss,
  },
  pedigree: {
    page: 'pedigree',
    'data-testid': 'nav-pedigree',
    to: '/tree/pedigree',
    external: true,
    wayfinding: true,
    linkName: 'Tree Subnav: Pedigree',
    label: 'subnav-legacy.subnav.pedigree-link',
    className: visibleLinkCss,
  },
  privatePeople: {
    page: 'private-people',
    'data-testid': 'nav-private-people',
    to: '/tree/private-people',
    external: true,
    wayfinding: true,
    linkName: 'Subnav: Private People',
    label: 'subnav-legacy.subnav.private-people-link',
    className: visibleLinkCss,
  },
  recents: {
    page: 'recents',
    as: 'button',
    moreAs: ListItem,
    type: 'button',
    'data-testid': 'recents-button',
    label: 'subnav-legacy.subnav.recents-button',
    className: buttonCss,
  },
  researchTree: {
    page: 'research-tree',
    'data-testid': 'nav-researcher-groups',
    to: '/groups/research-tree',
    external: true,
    wayfinding: true,
    linkName: 'Subnav: Researcher Groups',
    label: 'subnav-legacy.researcher-trees.label',
    className: visibleLinkCss,
  },
  researcherTreesFeedback: {
    page: 'researcher-trees-feedback',
    to: 'https://community.familysearch.org/en/group/338-researcher-trees-feedback-early-access',
    external: true,
    wayfinding: true,
    target: '_blank',
    label: 'subnav-legacy.give-feedback-researcher.label',
    listItemCss: endCss,
    className: linkCss,
  },
}

export default function useSubnavItems({
  recentsOverlay,
  page,
  showHelpOthers,
  canAccessFGT,
  canAccessCET,
  showGroupsNavigation,
  currentTreeId,
  isResearcherGroup,
}) {
  const { signedIn } = useUser()
  const [canAddPersonalData] = usePermission('CanAddPersonalDataPermission', 'FamilyTree')
  const [showTemple] = useShowTemple()
  const { isOn: useFamilyTreeUrl } = useFeatureFlag('tree-overview_useFamilyTreeUrl')

  subNavItems.overview.to = useFamilyTreeUrl ? `/${i18n.language}/family-tree/` : '/tree/overview/'

  return useMemo(() => {
    const filteredSubnavItems = []
    if (!showGroupsNavigation || page === 'overview') {
      filteredSubnavItems.push(subNavItems.overview)
    }
    filteredSubnavItems.push(subNavItems.pedigree)
    if (signedIn) {
      filteredSubnavItems.push({
        ...subNavItems.recents,
        // Add the onClick here since recentsOverlay is now in scope
        onClick: (event) => {
          trackEvent({ link_name: 'Tree Subnav: Recents' })
          recentsOverlay.handleClick(event)
        },
      })
    }
    filteredSubnavItems.push(
      {
        ...subNavItems.find,
        to: `/search/tree${isResearcherGroup && currentTreeId ? `/${COLLECTION_ID_IN_SEARCH_FORMAT}/${currentTreeId}` : ''}?from=tree`,
      },
      subNavItems.following,
      subNavItems.privatePeople
    )
    if (!currentTreeId) {
      filteredSubnavItems.push(subNavItems.contributions)
    }
    // We show Family Groups for ordinance sharing even if the user is in a blocked FGT country unless they specifically "cannot add personal data" (Russians)
    if ((canAccessFGT || showTemple) && canAddPersonalData) {
      filteredSubnavItems.push(subNavItems.familyGroups)
    }
    if (canAccessCET) {
      filteredSubnavItems.push(subNavItems.researchTree)
    }

    if (
      showGroupsNavigation &&
      page !== 'family' &&
      ((currentTreeId && isResearcherGroup) || page === 'research-tree')
    ) {
      filteredSubnavItems.push(subNavItems.researcherTreesFeedback)
    }
    if (showHelpOthers && !currentTreeId) {
      filteredSubnavItems.push({
        ...subNavItems.helper,
        redirectUrl: `/tree/${page}`,
      })
    }

    return filteredSubnavItems
  }, [
    canAddPersonalData,
    showGroupsNavigation,
    page,
    signedIn,
    isResearcherGroup,
    currentTreeId,
    canAccessFGT,
    showTemple,
    canAccessCET,
    showHelpOthers,
    recentsOverlay,
  ])
}
